import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* A BOX THAT TAKES A TYPED NAME AND OFFERS A DROPDOWN — Max, 2026-10-16: "làm dropdown chứ ko phải
   tab", on the persona list drawn as a row of chips under the Add box. The shared GTM list
   (personas, channels, regions) opens as a list under the box, narrowed by what is typed; ↑ ↓ move
   through it, Enter takes the lit one (or, with none lit, does what the box's own Enter does), Escape
   closes it.

   THE LIST IS A PORTAL, FIXED TO THE BOX. `.gtm-rank` clips (overflow hidden, for its rounded
   corners), and the Add box is its last row — a menu positioned inside it would be cut off at the
   frame. Fixed to the box's own rectangle and re-placed on every scroll and resize, as Daily Output's
   cell picker is; it opens upward when there is no room below. */
export default function GtmPickInput({
  value, onChange, options = [], onPick, onEnter, onEscape,
  placeholder, ariaLabel, emptyText, autoFocus = false, style, inputRef,
}) {
  const ref = useRef(null);
  const listId = `gtm-pick-${useId().replace(/:/g, '')}`;
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [hi, setHi] = useState(-1);
  const q = String(value || '').trim().toLowerCase();
  const shown = q ? options.filter((n) => n.toLowerCase().includes(q)) : options;

  useEffect(() => { setHi(-1); }, [q]);
  useEffect(() => {
    if (!open) return undefined;
    const place = () => setRect(ref.current ? ref.current.getBoundingClientRect() : null);
    place();
    const away = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', away);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', away);
    };
  }, [open]);

  const pick = (n) => { setOpen(false); onPick(n); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && hi >= 0 && shown[hi]) pick(shown[hi]);
      else { setOpen(false); if (onEnter) onEnter(); }
    } else if (e.key === 'Escape') {
      if (open) setOpen(false); else if (onEscape) onEscape();
    } else if (e.key === 'Tab') setOpen(false);
  };

  /* Nothing to offer for what is typed: no list at all — the note under the box says the name is
     new. With nothing typed and nothing left, the list says so. */
  const draw = open && rect && (shown.length > 0 || !q);
  let pos = null;
  if (draw) {
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const up = below < 180 && above > below;
    pos = {
      position: 'fixed', left: rect.left, width: Math.max(rect.width, 240),
      maxHeight: Math.min(280, Math.max(120, up ? above : below)),
      ...(up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    };
  }
  return (
    <>
      <input
        ref={(el) => { ref.current = el; if (inputRef) inputRef.current = el; }}
        value={value} placeholder={placeholder} aria-label={ariaLabel} autoFocus={autoFocus}
        role="combobox" aria-expanded={!!draw} aria-controls={listId} aria-autocomplete="list" autoComplete="off"
        style={style}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {draw && createPortal(
        <div ref={popRef} id={listId} className="gtm-pickpop" style={pos} role="listbox" aria-label={ariaLabel}>
          {shown.map((n, i) => (
            <button
              type="button" role="option" key={n} aria-selected={i === hi}
              className={`gtm-pickopt${i === hi ? ' hi' : ''}`}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(n)}
            >{n}</button>
          ))}
          {!shown.length && <div className="gtm-pickempty">{emptyText}</div>}
        </div>,
        document.body,
      )}
    </>
  );
}
